/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-41 (lee 24/10) — tab **Cài đặt ban đầu** `/cai-dat-ban-dau?tu=`, làm lại theo thiết kế NEMA_MESH_V02 (hình) và bản thử
 * bấm được `_BUS/thiet_ke/cai_dat_ban_dau/ban_thu_cai_dat_ban_dau.html` (hành vi). Thay trình tự từng bước của TK-40.
 *   · KHÔNG khoá bước: mọi nút luôn hiện, bấm theo thứ tự nào cũng được; thông tin chưa có thì không hiện.
 *   · Người quyết thời điểm: Nguồn AC KHÔNG đếm; mesh khi cài ĐẾM LÊN từ lúc ack; không có "hết giờ ⇒ tủ thường".
 *   · Hai nút mesh KHÁC nhau: Mở / Tắt mesh (nguon_host, ngay lúc này) · Kích hoạt mạng mesh (kich_hoat_mesh, SLCU ghi bộ nhớ).
 *   · Bảng node = bảng đã lưu ∪ lượt dò hiện tại, đối chiếu 4 nhãn (`doiChieu`); Lưu bảng hỏi ngay trong trang, CSDL chỉ đổi
 *     khi SLCU ack OK.
 * Bố cục: đường dẫn + chip MÔ PHỎNG · đầu trang · cột chính (tủ đang chọn + 4 ô · Nguồn AC | Nguồn mesh khi cài · Mesh khi khởi
 * động · Danh sách node đèn · Nhật ký lệnh & phản hồi · Thông tin đã nhận từ tủ) · cột phải 264 px (Chọn tủ cài đặt · Hai tác vụ).
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import { useEffect, useMemo, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Activity, ChevronRight, History, Info, MapPin, Network, Power, RotateCcw, Save, Search, Undo2, Zap, Map as MapIcon } from 'lucide-react';
import { ConfirmDialog, ErrorBox, Spinner, useToast } from '../components/ui';
import { XepViTri } from '../components/caidat/XepViTri';
import type { NhapXep } from '../lib/xepViTri';
import { useDatDoiTuong } from '../lib/doiTuongDangChon';
import { api, errorMessage, ApiError } from '../lib/api';
import {
  chuMesh, demMo, ketNoiMesh, doiChieu, dsLuu, giayHai, hex4, LOP_MAU_MESH, useCayCaiDat, useTuCaiDat, useViecMesh,
  type NhanDoiChieu, type TuCaiDat, type TuCaiDatCay, type ViecMesh,
} from '../lib/caiDatBanDau';

/* ───────── mảnh dùng chung (module, không khai trong thân vẽ) ───────── */
type Mau = 'tot' | 'canh' | 'nhan' | 'nguy' | 'xam';
const LOP_CHIP: Record<Mau, string> = {
  tot: 'border-tot/40 bg-tot/10 text-tot', canh: 'border-canh/40 bg-canh-nen text-canh', nhan: 'border-nhan/40 bg-nhan/10 text-nhan',
  nguy: 'border-nguy/40 bg-nguy-nen text-nguy', xam: 'border-vien-manh text-chu-2',
};
function Chip({ mau, children, nho = false }: { mau: Mau; children: ReactNode; nho?: boolean }) {
  return <span className={`inline-flex items-center whitespace-nowrap rounded border font-normal ${nho ? 'px-1.5 py-px text-[9px] tracking-wide' : 'px-2 py-[3px] text-[11px]'} ${LOP_CHIP[mau]}`}>{children}</span>;
}
type KieuNut = 'thuong' | 'chinh' | 'tot' | 'nguy';
const LOP_NUT: Record<KieuNut, string> = {
  thuong: 'border-vien-manh bg-nen-2 text-chu-1 hover:border-nhan-dam',
  chinh: 'border-nhan bg-nhan font-semibold text-nen-0 hover:bg-nhan-sang',
  tot: 'border-tot/40 bg-tot/10 text-tot hover:border-tot',
  nguy: 'border-nguy/40 bg-nguy-nen text-nguy hover:border-nguy',
};
function Nut({ kieu = 'thuong', nho = false, className = '', ...p }: ButtonHTMLAttributes<HTMLButtonElement> & { kieu?: KieuNut; nho?: boolean; 'data-o'?: string; 'data-loai'?: string }) {
  return <button type="button" {...p} className={`inline-flex items-center gap-2 rounded-[7px] border transition disabled:cursor-not-allowed disabled:opacity-45 ${nho ? 'min-h-[30px] px-2.5 py-1 text-xs' : 'min-h-[36px] px-3.5 py-2 text-[12.5px]'} ${LOP_NUT[kieu]} ${className}`} />;
}
const Khoi = ({ children, className = '', o }: { children: ReactNode; className?: string; o?: string }) =>
  <section className={`min-w-0 rounded-[10px] border border-vien bg-nen-1 ${className}`} data-o={o}>{children}</section>;
const MoMat = ({ children }: { children: ReactNode }) => <div className="mb-1.5 text-[10.5px] uppercase tracking-[0.14em] text-chu-2">{children}</div>;
function TheDau({ icon, tieu, phu }: { icon: ReactNode; tieu: string; phu: string }) {
  return (
    <div className="mb-4 flex items-start gap-3">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[7px] border border-vien-manh text-chu-1">{icon}</span>
      <div><h3 className="m-0 text-[15px] font-semibold text-chu-1">{tieu}</h3><p className="m-0 mt-0.5 text-xs text-chu-2">{phu}</p></div>
    </div>
  );
}
const Ghi = ({ children }: { children: ReactNode }) => <p className="m-0 mt-3.5 text-[11.5px] text-chu-2"><span className="mr-1.5 text-chu-3">•</span>{children}</p>;
const NHAN_DC: Record<NhanDoiChieu, { chu: string; mau: Mau }> = {
  moi: { chu: 'Node mới', mau: 'nhan' }, 'doi-lien-ket': { chu: 'Đổi liên kết', mau: 'canh' },
  'chua-thay': { chu: 'Đã lưu · Chưa thấy lượt này', mau: 'xam' }, 'da-luu': { chu: 'Đã lưu', mau: 'tot' },
};
const gioVN = (s: string) => new Date(new Date(s).getTime() + 7 * 3600_000).toISOString().slice(11, 19);
/** "Đã cài" = có bảng đèn đã lưu VÀ đã kích hoạt mesh; thiếu một ⇒ vàng (phiếu §4). */
const daCai = (soDenLuu: number, kichHoat: boolean | null) => soDenLuu > 0 && kichHoat === true;
type Lam = (viec: ViecMesh, than?: unknown) => void;
type V = ReturnType<typeof useViecMesh>;

/* ───────── cột phải: chọn tủ ───────── */
function TheTu({ t, chon, onChon }: { t: TuCaiDatCay; chon: boolean; onChon: () => void }) {
  return (
    <button type="button" onClick={onChon} aria-pressed={chon} data-the-tu={t.id}
      className={`mx-2 mb-1.5 flex w-[calc(100%-1rem)] items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left ${chon ? 'border-nhan-dam bg-nen-3' : 'border-transparent hover:bg-nen-2'}`}>
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${t.online ? 'bg-tot' : 'bg-chu-3'}`} />
      <span className="min-w-0 flex-1"><b className="block truncate text-[12.5px] font-semibold text-chu-1">{t.name}</b>
        <small className="text-[10.5px] text-chu-2">{t.tuThuong ? 'Tủ thường' : t.soDenLuu ? `${t.soDenLuu} đèn đã lưu` : 'Chưa có bảng đèn'}</small></span>
      {t.tuThuong ? <Chip mau="xam">Tủ thường</Chip>
        : <Chip mau={daCai(t.soDenLuu, t.meshKichHoat) ? 'tot' : 'canh'}>{t.soDenLuu ? 'Chỉnh sửa' : 'Cài mới'}</Chip>}
    </button>
  );
}
function ChonTu({ chon, datChon }: { chon: string | null; datChon: (id: string) => void }) {
  const cay = useCayCaiDat();
  const [tim, setTim] = useState('');
  const [mo, setMo] = useState<Set<string>>(new Set());
  const ds = useMemo(() => cay.data ?? [], [cay.data]);
  useEffect(() => {   // cây thu gọn — chỉ mở tuyến của tủ đang chọn
    const r = ds.find((x) => x.tu.some((t) => t.id === chon));
    if (r) setMo((s) => (s.has(r.id) ? s : new Set(s).add(r.id)));
  }, [ds, chon]);
  const soTu = ds.reduce((n, r) => n + r.tu.length, 0);
  const k = tim.trim().toLowerCase();
  return (
    <Khoi o="chon-tu-cai-dat">
      <div className="border-b border-vien px-4 pb-3 pt-4"><h3 className="m-0 text-[15px] font-semibold text-chu-1">Chọn tủ cài đặt</h3><small className="text-[11.5px] text-chu-2">{ds.length} tuyến · {soTu} tủ</small></div>
      <input value={tim} onChange={(e) => setTim(e.target.value)} placeholder="Tìm tuyến, tủ..." aria-label="Tìm tuyến, tủ"
        className="mx-4 mb-1 mt-3 w-[calc(100%-2rem)] rounded-[7px] border border-vien-manh bg-nen-lom px-3 py-2 text-sm text-chu-1 placeholder:text-chu-3" />
      {cay.isLoading && <div className="p-4"><Spinner /></div>}
      {cay.isError && <div className="p-3"><ErrorBox error={cay.error} onRetry={() => void cay.refetch()} /></div>}
      {ds.map((r) => {
        const cac = r.tu.filter((t) => !k || `${t.name} ${t.code ?? ''} ${r.tenDayDu}`.toLowerCase().includes(k));
        if (!cac.length) return null;
        const m = k ? true : mo.has(r.id);
        return (
          <div key={r.id}>
            <button type="button" aria-expanded={m} onClick={() => setMo((s) => { const n = new Set(s); if (n.has(r.id)) n.delete(r.id); else n.add(r.id); return n; })}
              className="flex w-full items-center gap-2 px-4 pb-2 pt-3 text-left text-[11.5px] text-chu-2" data-nhom-tuyen={r.id}>
              <ChevronRight className={`h-3 w-3 shrink-0 text-chu-3 transition ${m ? 'rotate-90' : ''}`} /><MapIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 truncate">{r.tenDayDu}</span>
            </button>
            {m && cac.map((t) => <TheTu key={t.id} t={t} chon={t.id === chon} onChon={() => datChon(t.id)} />)}
          </div>
        );
      })}
      <div className="mt-2 border-t border-vien px-4 pb-4 pt-3 text-[11px] leading-[1.7] text-chu-2">
        Chọn tủ để thấy toàn bộ tác vụ.<br /><b className="font-medium text-chu-1">Chưa cài → Cài mới</b><br /><b className="font-medium text-chu-1">Đã lưu bảng → Chỉnh sửa</b>
      </div>
    </Khoi>
  );
}

/* ───────── cột chính ───────── */
function TuDangChon({ tu, v, lam }: { tu: TuCaiDat; v: V; lam: Lam }) {
  const soNgo = tu.ngoRa.filter((o) => o.isOn).length;
  const coDoi = tu.bang.some((h) => h.parentLuu === null || (h.parentLuot !== null && h.parentLuot !== h.parentLuu));
  const cai = daCai(tu.soDenLuu, tu.meshKichHoat);
  const kn = ketNoiMesh(tu);
  const mCai = chuMesh(kn, tu.hostBat, 'cai'), mKhoi = chuMesh(kn, tu.meshKichHoat, 'khoi-dong');
  const o = [
    { nhan: 'Nguồn AC', gia: `${soNgo}/3 ngõ đang bật`, mau: soNgo ? 'text-tot' : 'text-chu-2' },
    /* TK-44 §3: hai ô mesh đọc TỦ BÁO — một hàm chữ (`chuMesh`): mất kết nối ⇒ xám, tủ không khai ⇒ "—" */
    { nhan: 'Mesh khi cài', gia: mCai.chu, mau: LOP_MAU_MESH[mCai.mau] },
    { nhan: 'Mesh khi khởi động', gia: mKhoi.chu, mau: LOP_MAU_MESH[mKhoi.mau] },
    { nhan: 'Bảng đèn', gia: tu.bang.length ? `${tu.bang.length} node · ${coDoi || !tu.soDenLuu ? 'Chưa lưu' : 'Đã lưu'}` : 'Chưa có node', mau: coDoi || (!tu.soDenLuu && tu.bang.length) ? 'text-canh' : tu.soDenLuu ? 'text-tot' : 'text-chu-2' },
  ];
  return (
    <Khoi o="tu-dang-chon">
      <div className="flex flex-wrap items-start gap-3 px-[18px] pb-4 pt-[18px]">
        <div className="min-w-0">
          <MoMat>Tủ đang chọn</MoMat>
          <h2 className="m-0 mb-1.5 mt-1 flex flex-wrap items-center gap-2.5 text-[19px] font-semibold text-chu-1">
            Tủ {tu.name}{' '}
            {tu.soDenLuu ? <Chip mau={cai ? 'tot' : 'canh'}>Đã lưu bảng · Chỉnh sửa</Chip> : <Chip mau="canh">Chưa cài · Cài mới</Chip>}
          </h2>
          {tu.tuyen && <div className="text-xs text-chu-2">{tu.tuyen}</div>}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2.5">
          {tu.giuTay && <Chip mau="canh">Đang giữ tay</Chip>}
          <Nut onClick={() => lam('tra-quyen')} disabled={v.ban} data-o="nut-tra-quyen"><Undo2 className="h-3.5 w-3.5" />{v.dang('tra-quyen') ? 'Đang gửi…' : 'Trả quyền AUTO LOCAL'}</Nut>
        </div>
      </div>
      <div className="grid grid-cols-2 border-t border-vien md:grid-cols-4" data-o="bon-o">
        {o.map((x, i) => (
          <div key={x.nhan} className={`min-w-0 px-[18px] py-3.5 ${i % 2 === 0 ? 'border-r border-vien' : ''} ${i === 1 ? 'md:border-r md:border-vien' : ''} ${i < 2 ? 'border-b border-vien md:border-b-0' : ''}`}>
            <small className="mb-1.5 block text-xs text-chu-2">{x.nhan}</small><b className={`text-lg font-semibold ${x.mau}`}>{x.gia}</b>
          </div>
        ))}
      </div>
    </Khoi>
  );
}

function KhoiAc({ tu, v, lam }: { tu: TuCaiDat; v: V; lam: Lam }) {
  return (
    <Khoi className="p-[18px]" o="nguon-ac">
      <TheDau icon={<Zap className="h-4 w-4" />} tieu="Nguồn AC của đèn" phu="Bật/tắt từng ngõ theo nhu cầu thực tế" />
      <div className="grid grid-cols-3 gap-2.5">
        {[1, 2, 3].map((i) => {
          const o = tu.ngoRa.find((x) => x.index === i);
          return (
            <button key={i} type="button" disabled={v.ban || !o} data-ngo={i} onClick={() => lam('ngo', { outputs: [{ index: i, on: !o?.isOn }] })}
              className={`flex flex-col items-center gap-0.5 rounded-[7px] border px-1.5 py-3 disabled:opacity-60 ${o?.isOn ? 'border-tot/40 bg-tot/10' : 'border-vien-manh bg-nen-2'}`}>
              <b className={`text-sm font-semibold ${o?.isOn ? 'text-tot' : 'text-chu-1'}`}>Ngõ {i}</b>
              <small className={`text-[11.5px] ${o?.isOn ? 'text-tot' : 'text-chu-2'}`}>{v.dang('ngo') ? 'Đang gửi…' : !o ? '—' : o.isOn ? 'Đang bật' : 'Đang tắt'}</small>
            </button>
          );
        })}
      </div>
      <Ghi>Trạng thái theo phản hồi tủ. Không tự đặt thời gian chờ AC.</Ghi>
    </Khoi>
  );
}

function KhoiMesh({ tu, v, lam, now }: { tu: TuCaiDat; v: V; lam: Lam; now: number }) {
  const mo = tu.hostBat === true;
  return (
    <Khoi className="p-[18px]" o="nguon-mesh">
      <TheDau icon={<Network className="h-4 w-4" />} tieu="Nguồn mesh khi cài" phu="Điều khiển nguồn NEMA-host ngay lúc này" />
      <div className="flex flex-wrap items-center gap-[18px]">
        <Nut kieu={mo ? 'tot' : 'chinh'} className="!px-[22px] !py-3 text-[13.5px] font-semibold" disabled={v.ban} onClick={() => lam('mesh', { bat: !mo })} data-o="nut-mesh">
          <Power className="h-4 w-4" />{v.dang('mesh') ? 'Đang gửi…' : mo ? 'Tắt mesh' : 'Mở mesh'}
        </Nut>
        {mo && (
          <div><small className="block text-[11.5px] text-chu-2">Thời gian host đã mở</small>
            <b className="so-do text-xl font-semibold text-chu-1" data-o="dem-mesh">{tu.meshMoLuc ? demMo((now - new Date(tu.meshMoLuc).getTime()) / 1000) : '—'}</b></div>
        )}
      </div>
      <Ghi>{!tu.online ? 'Mất kết nối' : tu.choTuBao ? 'Đang chờ tủ báo' : tu.hostBat === null ? '—' : !mo ? 'Host đang tắt nguồn' : tu.host?.daBao ? 'Host đã báo mở nguồn · bạn tự quyết định khi nào dò' : 'Đã cấp nguồn host · chờ host báo'}</Ghi>
    </Khoi>
  );
}

function KhoiKhoiDong({ tu, v, lam }: { tu: TuCaiDat; v: V; lam: Lam }) {
  const kh = tu.meshKichHoat === true;
  return (
    <Khoi className="flex flex-wrap items-center gap-3 px-[18px] py-4" o="mesh-khoi-dong">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[7px] border border-vien-manh text-chu-1"><History className="h-4 w-4" /></span>
      <div className="min-w-[220px] flex-1"><h3 className="m-0 text-[15px] font-semibold text-chu-1">Mesh khi tủ khởi động</h3>
        <p className={`m-0 mt-0.5 text-xs ${!tu.online || tu.choTuBao || tu.meshKichHoat === null ? 'text-chu-3' : 'text-chu-2'}`} data-o="mesh-khoi-dong-chu">{!tu.online ? 'Mất kết nối' : tu.choTuBao ? 'Đang chờ tủ báo' : tu.meshKichHoat === null ? '—' : kh ? 'Đã kích hoạt — lần khởi động sau, SLCU tự cấp nguồn NEMA-host.' : 'Chưa kích hoạt — tủ khởi động sẽ không cấp nguồn NEMA-host.'}</p></div>
      <Nut kieu={kh ? 'tot' : 'chinh'} disabled={v.ban} onClick={() => lam('kich-hoat', { bat: !kh })} data-o="nut-kich-hoat">
        {v.dang('kich-hoat') ? 'Đang gửi…' : kh ? 'Hủy kích hoạt khởi động' : 'Kích hoạt mạng mesh'}
      </Nut>
    </Khoi>
  );
}

function KhoiNode({ tu, v, lam, loi, datLoi, loai, setLoai, onXep }: {
  tu: TuCaiDat; v: V; lam: (viec: ViecMesh, than?: unknown) => Promise<unknown>; loi: string | null; datLoi: (s: string | null) => void;
  /** TK-44 §5: "Loại khỏi bảng" sống ở TRANG — cùng bản nháp với "Loại bỏ" của chế độ xếp */
  loai: Set<number>; setLoai: (s: Set<number>) => void;
  onXep: () => void;
}) {
  const toast = useToast();
  const [hoi, setHoi] = useState(false);
  const [choLuu, setChoLuu] = useState<string | null>(null);   // id lệnh luu_ds đang chờ ack
  useEffect(() => { setHoi(false); setChoLuu(null); }, [tu.id]);
  const coLuot = tu.luot !== null;
  const nhan = tu.bang.map((h) => doiChieu(h, coLuot));
  const soMoi = nhan.filter((x) => x === 'moi').length, soDoi = nhan.filter((x) => x === 'doi-lien-ket').length;
  const soChua = nhan.filter((x) => x === 'chua-thay').length, soNhan = tu.bang.filter((h) => h.parentLuot !== null).length;
  const coDoi = soMoi + soDoi + loai.size > 0;
  /* kết quả Lưu bảng: theo trạng thái lệnh luu_ds máy chủ trả (ack OK mới đổi CSDL) */
  useEffect(() => {
    if (!choLuu || tu.luuCuoi?.id !== choLuu) return;
    if (tu.luuCuoi.state === 'ACK_OK') { setChoLuu(null); setLoai(new Set()); toast.success(`Tủ đã lưu bảng · Bản lưu #${tu.banLuu}`); }
    else if (tu.luuCuoi.state === 'ACK_LOI' || tu.luuCuoi.state === 'HET_HAN') {
      setChoLuu(null);
      datLoi(`Tủ không lưu được bảng (${tu.luuCuoi.ack?.result ?? tu.luuCuoi.state}${tu.luuCuoi.ack?.detail ? ` · ${tu.luuCuoi.ack.detail}` : ''}) — chưa đổi gì`);
    }
  }, [tu.luuCuoi, choLuu, tu.banLuu, toast, datLoi, setLoai]);
  const luu = () => {
    setHoi(false); datLoi(null);
    lam('luu-bang', { ds: dsLuu(tu.bang, loai) }).then((r) => setChoLuu((r as { lenh: string }).lenh)).catch((e) => datLoi(errorMessage(e)));
  };
  const quet = () => Promise.all([api.post(`/api/nema/tu/${tu.id}/lenh`, { kind: 'quet_dien' }), api.post(`/api/nema/tu/${tu.id}/lenh`, { kind: 'quet_gps' })])
    .then(() => toast.success('Đã gửi quét thông số')).catch((e) => toast.error('Không quét được', errorMessage(e)));
  const TH = 'whitespace-nowrap border-y border-vien px-[18px] py-2.5 align-top font-semibold text-chu-1';
  const TD = 'whitespace-nowrap border-b border-vien px-[18px] py-3';
  return (
    <Khoi o="danh-sach-node">
      <div className="flex flex-wrap items-end gap-3 border-b border-vien px-[18px] pb-3.5 pt-[18px]">
        <div><MoMat>Topology / node con → node cha</MoMat>
          <h3 className="m-0 mt-0.5 flex items-center gap-2 text-base font-semibold text-chu-1">Danh sách node đèn <span className="rounded border border-nhan/40 bg-nhan/10 px-1.5 text-[11px] text-nhan">{tu.bang.length}</span></h3></div>
        <div className="ml-auto flex flex-wrap items-center gap-3.5">
          {tu.luot && <span className="text-xs text-chu-2" data-o="luot-do">Lượt dò {tu.luot.lan} · {soNhan} node đã nhận</span>}
          <Nut kieu="chinh" disabled={v.ban} onClick={() => { datLoi(null); lam('do-tim').catch((e) => datLoi(e instanceof ApiError ? e.message : errorMessage(e))); }} data-o="nut-do-tim">
            <Search className="h-3.5 w-3.5" />{v.dang('do-tim') ? 'Đang gửi…' : 'Dò tìm'}
          </Nut>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 px-[18px] py-3" data-o="chip-dem">
        {soMoi > 0 && <Chip mau="nhan">{soMoi} node mới</Chip>}
        {soDoi > 0 && <Chip mau="canh">{soDoi} đổi liên kết</Chip>}
        {soChua > 0 && <Chip mau="xam">{soChua} chưa thấy lại</Chip>}
        {loai.size > 0 && <Chip mau="nguy">{loai.size} loại khỏi bảng</Chip>}
        <div className="ml-auto flex flex-wrap gap-2">
          <Nut disabled={!coDoi} onClick={() => { setLoai(new Set()); setHoi(false); }}><RotateCcw className="h-3.5 w-3.5" />Bỏ thay đổi</Nut>
          <Nut disabled={!tu.bang.length || v.ban || !!choLuu} onClick={() => setHoi(true)} data-o="nut-luu-bang"><Save className="h-3.5 w-3.5" />{choLuu ? 'Đang chờ tủ…' : 'Lưu bảng'}</Nut>
          <Nut disabled={v.ban} onClick={quet} data-o="nut-quet"><Activity className="h-3.5 w-3.5" />Quét thông số</Nut>
          {/* TK-44 §5: xếp vị trí NGAY TRONG trang này (`?xep=1`) — không còn sang /ban-do */}
          <Nut onClick={onXep} data-o="nut-xep-vi-tri"><MapPin className="h-3.5 w-3.5" />Xếp vị trí</Nut>
        </div>
      </div>
      {loi && <div className="mx-[18px] mb-3 rounded-[7px] border border-nguy/40 bg-nguy-nen px-3 py-2 text-[12.5px] text-nguy" data-o="loi-trang">{loi}</div>}
      {hoi && (
        <div className="mx-[18px] mb-3 flex flex-wrap items-center gap-2.5 rounded-[7px] border border-vien-manh bg-nen-2 px-3 py-2.5 text-[12.5px] text-chu-1" data-o="hoi-luu">
          <span>Lưu <b>{tu.bang.length - loai.size}</b> node{soMoi ? <> · <b>{soMoi}</b> mới</> : null}{soDoi ? <> · <b>{soDoi}</b> đổi liên kết</> : null}{loai.size ? <> · loại <b>{loai.size}</b></> : null} xuống SLCU?</span>
          <Nut kieu="chinh" nho onClick={luu} data-o="nut-luu-xac-nhan">Lưu</Nut><Nut nho onClick={() => setHoi(false)}>Huỷ</Nut>
        </div>
      )}
      {tu.bang.length ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead><tr className="text-left text-xs">
              <th className={TH}>ID gốc<small className="mt-0.5 block text-[10.5px] font-normal text-chu-2">Node con</small></th>
              <th className={TH}>ID liên kết<small className="mt-0.5 block text-[10.5px] font-normal text-chu-2">Node cha</small></th>
              <th className={TH}>Tìm được lúc<small className="mt-0.5 block text-[10.5px] font-normal text-chu-2">Từ lần bấm Dò tìm</small></th>
              <th className={TH}>Đối chiếu với bảng đã lưu</th>
              <th className={`${TH} text-right`}>Thao tác</th>
            </tr></thead>
            <tbody>
              {tu.bang.map((h, i) => {
                const x = loai.has(h.addr); const dc = NHAN_DC[nhan[i]]; const p = h.parentLuot ?? h.parentLuu ?? 0;
                return (
                  <tr key={h.addr} className={x ? 'opacity-45 [&_td]:line-through' : ''} data-node={hex4(h.addr)} data-nhan={nhan[i]}>
                    <td className={`${TD} font-mono font-medium text-chu-1`}>{hex4(h.addr)}</td>
                    <td className={`${TD} font-mono font-medium text-chu-1`}>
                      {hex4(p)}{p === 0 && <span className="ml-1.5"><Chip mau="xam" nho>HOST</Chip></span>}
                      {nhan[i] === 'doi-lien-ket' && <small className="block font-sans text-[10.5px] font-normal text-chu-2">đã lưu: {hex4(h.parentLuu!)}</small>}
                    </td>
                    <td className={`${TD} font-mono text-chu-1`} data-thay={h.thayMs ?? ''}>
                      {giayHai(h.thayMs)}{h.parentLuot === null && coLuot && <small className="block font-sans text-[10.5px] text-chu-2">Chưa nhận ở lượt hiện tại</small>}
                    </td>
                    <td className={TD}><Chip mau={dc.mau}>{dc.chu}</Chip></td>
                    <td className={`${TD} text-right`}>
                      <Nut nho kieu={x ? 'thuong' : 'nguy'} onClick={() => { const n = new Set(loai); if (n.has(h.addr)) n.delete(h.addr); else n.add(h.addr); setLoai(n); }} data-loai={hex4(h.addr)}>
                        {x ? 'Giữ lại' : 'Loại khỏi bảng'}
                      </Nut>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <div className="px-[18px] py-8 text-center text-chu-3">Chưa có node. Bật nguồn AC, mở mesh, rồi bấm Dò tìm.</div>}
      <div className="flex flex-wrap justify-between gap-3 px-[18px] py-3 text-[11px] text-chu-2" data-o="chan-bang">
        <span>{coDoi ? 'Có thay đổi dự thảo. Lưu khi bạn đã đối chiếu đủ; không tự xóa/reset thiết bị RF.' : tu.soDenLuu ? 'Bảng khớp bản đã lưu.' : 'Chưa có bản lưu.'}</span>
        <span data-o="ban-luu">{tu.banLuu ? `Bản lưu #${tu.banLuu}` : ''}</span>
      </div>
    </Khoi>
  );
}

function KhoiNhatKy({ tu }: { tu: TuCaiDat }) {
  return (
    <Khoi o="nhat-ky-lenh">
      <div className="flex flex-wrap items-center gap-3 border-b border-vien px-[18px] py-4">
        <h3 className="m-0 text-[15px] font-semibold text-chu-1">Nhật ký lệnh &amp; phản hồi</h3>
        <div className="ml-auto flex gap-3.5 text-[11px] text-chu-2">
          <span><i className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-nhan align-middle" />Phần mềm gửi</span>
          <span><i className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-tot align-middle" />Tủ / host trả về</span>
        </div>
      </div>
      <div className="flex max-h-[150px] flex-col gap-[9px] overflow-auto px-[18px] py-3 font-mono text-[11.5px]">
        {tu.nhatKy.length ? tu.nhatKy.map((d, i) => (
          <div key={i} data-nk={d.loai}><span className="mr-2.5 text-chu-3">{gioVN(d.luc)}</span><span className={d.loai === 'gui' ? 'text-nhan' : 'text-tot'}>{d.chu}</span></div>
        )) : <div className="text-chu-3">—</div>}
      </div>
    </Khoi>
  );
}

function KhoiThongTin({ tu }: { tu: TuCaiDat }) {
  const host = !tu.online ? 'Mất kết nối' : tu.choTuBao ? 'Đang chờ tủ báo' : tu.hostBat === null ? '—' : !tu.hostBat ? 'Tắt nguồn' : tu.host?.daBao ? 'Đang mở nguồn' : 'Chưa báo';
  return (
    <Khoi className="px-[18px] py-4" o="thong-tin-tu">
      <h3 className="m-0 mb-2.5 flex items-center gap-2 text-sm font-semibold text-chu-1"><Info className="h-4 w-4" />Thông tin đã nhận từ tủ</h3>
      <div className="flex flex-wrap gap-x-[22px] gap-y-1 text-[11.5px] text-chu-2">
        <span>HOST: <b className="font-normal text-chu-1">{host}</b></span>
        {tu.host?.kenh != null && <span>Kênh ghi nhận: <b className="so-do font-normal text-chu-1">{hex4(tu.host.kenh)}</b></span>}
        {tu.host?.pan != null && <span>PANID ghi nhận: <b className="so-do font-normal text-chu-1">{hex4(tu.host.pan)}</b></span>}
        {tu.giaLap && <span>Phản hồi từ tủ giả lập</span>}
      </div>
    </Khoi>
  );
}

export default function CaiDatBanDauPage() {
  const [sp, setSp] = useSearchParams();
  const cay = useCayCaiDat();
  const dauTien = useMemo(() => cay.data?.flatMap((r) => r.tu)[0]?.id ?? null, [cay.data]);
  /* hành vi theo bản thử: mở trang là chọn sẵn tủ đầu tiên; `?tu=` giữ tủ đang chọn */
  const tuId = sp.get('tu') ?? dauTien;
  const q = useTuCaiDat(tuId);
  /* TK-44 (kiểm L2): tủ mất kết nối (hay chưa biết) ⇒ MỌI nút gửi xuống tủ khoá — một chỗ, mọi khối đọc `v.ban` */
  const v0 = useViecMesh(tuId);
  const v: V = { ...v0, ban: v0.ban || q.data?.online !== true };
  const toast = useToast();
  const [loi, setLoi] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { document.title = 'Cài đặt ban đầu — SDKD'; }, []);
  useEffect(() => { setLoi(null); }, [tuId]);
  const lam = (viec: ViecMesh, than?: unknown) => v.lam(viec, than).catch((e) => { if (viec !== 'do-tim' && viec !== 'luu-bang') toast.error('Không gửi được', errorMessage(e)); throw e; });
  const lamIm: Lam = (viec, than) => { lam(viec, than).catch(() => undefined); };
  const tu = q.data;
  useDatDoiTuong({ tuyen: tu?.tuyen, tu: tu?.name });   // TK-45 §2: cả chế độ xếp
  /* TK-44 §5: MỘT bản nháp cho tủ đang cài — "Loại khỏi bảng" (bảng node) = "Loại bỏ" (xếp); tên + vị trí của chế độ xếp */
  const [loai, setLoai] = useState<Set<number>>(new Set());
  const [nhap, setNhap] = useState<Map<number, NhapXep>>(new Map());
  useEffect(() => { setLoai(new Set()); setNhap(new Map()); }, [tuId]);
  const xep = sp.get('xep') === '1';
  const navigate = useNavigate();
  const [hoiRoi, setHoiRoi] = useState<string | null>(null);   // 've' = về bảng node; 'lui' = nút Back; còn lại = đường đích
  const chuaLuu = xep && nhap.size > 0;
  const veBang = () => { if (tuId) setSp({ tu: tuId }); };
  /* rời trang khi còn thay đổi xếp chưa lưu (menu, link, đổi tủ) ⇒ hỏi trong trang; tải lại / đóng tab ⇒ hộp của trình duyệt */
  useEffect(() => {
    if (!chuaLuu) return;
    const bam = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank') return;
      const u = new URL(a.href, window.location.href);
      if (u.origin !== window.location.origin) return;
      e.preventDefault(); e.stopPropagation(); setHoiRoi(u.pathname + u.search);
    };
    const dong = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    document.addEventListener('click', bam, true); window.addEventListener('beforeunload', dong);
    return () => { document.removeEventListener('click', bam, true); window.removeEventListener('beforeunload', dong); };
  }, [chuaLuu]);
  /* TK-44 (kiểm L3): nút Back của trình duyệt (vuốt về trên iPad) — còn thay đổi ⇒ chèn MỘT mục lịch sử canh cùng URL; Back chỉ bỏ
     mục canh (ở lại trang) ⇒ hỏi. Hết thay đổi (Lưu / Hủy) ⇒ tự bỏ mục canh. */
  const canh = useRef(false);
  useEffect(() => {
    if (!chuaLuu) { if (canh.current) { canh.current = false; window.history.back(); } return; }
    if (!canh.current) { window.history.pushState(window.history.state, '', window.location.href); canh.current = true; }
    const lui = () => { canh.current = false; setHoiRoi('lui'); };
    window.addEventListener('popstate', lui);
    return () => window.removeEventListener('popstate', lui);
  }, [chuaLuu]);
  const hoiRoiTrang = (
    <ConfirmDialog open={hoiRoi !== null} danger={false} title="Còn thay đổi chưa lưu" message="Tên / vị trí chưa lưu sẽ mất."
      confirmText="Rời trang" cancelText="Hủy"
      onCancel={() => { if (hoiRoi === 'lui') { window.history.pushState(window.history.state, '', window.location.href); canh.current = true; } setHoiRoi(null); }}
      onConfirm={() => {
        const d = hoiRoi, thay = canh.current; canh.current = false; setHoiRoi(null); setNhap(new Map());
        /* mục canh còn trên đầu ⇒ đường đi THAY nó (không để lại một mục trùng trong lịch sử) */
        if (d === 'lui') window.history.back(); else if (d === 've') { if (tuId) setSp({ tu: tuId }, { replace: thay }); } else if (d) navigate(d, { replace: thay });
      }} />
  );
  if (xep && tu) {
    return (
      <>
        <XepViTri tu={tu} loai={loai} nhap={nhap} setNhap={setNhap} onVe={() => (chuaLuu ? setHoiRoi('ve') : veBang())}
          doiLoai={(a) => { const n = new Set(loai); if (n.has(a)) n.delete(a); else n.add(a); setLoai(n); }} />
        {hoiRoiTrang}
      </>
    );
  }
  return (
    <div>
      <div className="-mt-1 mb-4 flex flex-wrap items-center gap-3 border-b border-vien pb-3" data-o="thanh-tren-trang">
        <div className="text-xs text-chu-2">Trung tâm điều hành<span className="mx-2 text-chu-3">/</span>Cài đặt ban đầu</div>
        {tu?.giaLap && <span className="ml-auto inline-flex items-center rounded border border-nhan/40 bg-nhan/10 px-2 py-1 text-[10.5px] tracking-[0.08em] text-nhan" data-o="chip-mo-phong"><span className="mr-2 inline-block h-[5px] w-[5px] rounded-full bg-nhan" />MÔ PHỎNG THIẾT BỊ</span>}
      </div>
      <div className="mb-[22px]">
        <MoMat>Thiết lập thiết bị / Mạng mesh</MoMat>
        <h1 className="m-0 mb-1.5 text-2xl font-semibold text-chu-1">Cài đặt ban đầu</h1>
        <p className="m-0 text-chu-2">Chọn tủ, thao tác chủ động, đối chiếu topology và lưu khi bạn xác nhận đủ.</p>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-5 min-[1180px]:grid-cols-[minmax(0,1fr)_264px]" data-vung="luoi-cai-dat">
        <div className="flex min-w-0 flex-col gap-4" data-o="cot-chinh">
          {!tuId && !cay.isLoading && <Khoi className="px-4 py-8 text-center text-sm text-chu-2">Chưa có tủ nào trong phạm vi tài khoản</Khoi>}
          {tuId && q.isLoading && <Spinner />}
          {tuId && q.isError && <ErrorBox error={q.error} onRetry={() => void q.refetch()} />}
          {tu && (
            <>
              <TuDangChon tu={tu} v={v} lam={lamIm} />
              <div className="grid grid-cols-[minmax(0,1fr)] gap-4 md:grid-cols-2">
                <KhoiAc tu={tu} v={v} lam={lamIm} />
                <KhoiMesh tu={tu} v={v} lam={lamIm} now={now} />
              </div>
              <KhoiKhoiDong tu={tu} v={v} lam={lamIm} />
              <KhoiNode tu={tu} v={v} lam={lam} loi={loi} datLoi={setLoi} loai={loai} setLoai={setLoai} onXep={() => tuId && setSp({ tu: tuId, xep: '1' })} />
              <KhoiNhatKy tu={tu} />
              <KhoiThongTin tu={tu} />
            </>
          )}
        </div>
        <aside className="order-first flex min-w-0 flex-col gap-4 min-[1180px]:order-none" data-o="cot-phai">
          <ChonTu chon={tuId} datChon={(id) => setSp({ tu: id }, { replace: true })} />
          <div className="hidden px-1 min-[1180px]:block" data-o="hai-tac-vu">
            <h4 className="m-0 my-3 text-[12.5px] font-semibold text-chu-1">Hai tác vụ khác nhau</h4>
            <div className="mb-3.5 flex gap-2.5 text-[11.5px] text-chu-2"><Power className="mt-0.5 h-3.5 w-3.5 shrink-0 text-chu-1" /><span><b className="block font-medium text-chu-1">Mở / tắt mesh</b>Cấp hoặc ngắt nguồn host trong lúc cài.</span></div>
            <div className="mb-3.5 flex gap-2.5 text-[11.5px] text-chu-2"><History className="mt-0.5 h-3.5 w-3.5 shrink-0 text-chu-1" /><span><b className="block font-medium text-chu-1">Kích hoạt mạng mesh</b>Lưu để tủ tự cấp nguồn host khi khởi động.</span></div>
          </div>
        </aside>
      </div>
    </div>
  );
}
